/** @module The green rectangle over the camera: drag it where the card lies, resize it there. */
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import type { Readiness } from "@/features/scan/auto-capture";
import { CARD_ASPECT, type Rect } from "@/features/scan/guide-frame";

type State = Readiness | "off";

const COLOURS: Record<State, string> = {
  off: "var(--text-faint)",
  empty: "var(--gain)",
  taken: "var(--text-faint)",
  moving: "var(--warn)",
  settling: "var(--gain)",
  ready: "var(--gain)",
};

/** Keep at least this much of the frame inside the picture. */
const MIN_SIZE = 0.15;

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** Move the rectangle, keeping it inside the picture. */
export function moved(rect: Rect, dx: number, dy: number): Rect {
  return {
    ...rect,
    x: clamp(rect.x + dx, 0, 1 - rect.width),
    y: clamp(rect.y + dy, 0, 1 - rect.height),
  };
}

/**
 * Resize from the bottom right corner, keeping the shape it has on screen.
 *
 * The rectangle lives in fractions of the picture, so a card shape is not 63:88 here;
 * the ratio the caller passes is the one the frame already has.
 */
export function resized(rect: Rect, dx: number, ratio: number): Rect {
  const width = clamp(rect.width + dx, MIN_SIZE, 1 - rect.x);
  const height = clamp(width / ratio, MIN_SIZE, 1 - rect.y);
  return { ...rect, width: height * ratio, height };
}

export function GuideFrame({
  readiness,
  progress,
  rect,
  onMove,
  onResize,
  onPan,
}: {
  readiness: State;
  progress: number;
  /** Where the detector looks, in fractions of the video; drawn exactly there. */
  rect: Rect | null;
  /** Steps of a drag, in fractions of the picture; the screen adds them up itself. */
  onMove?: (dx: number, dy: number) => void;
  onResize?: (dx: number, ratio: number) => void;
  /** Dragging next to the frame moves the visible cut-out instead of the frame. */
  onPan?: (dx: number, dy: number) => void;
}) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement | null>(null);
  const colour = COLOURS[readiness];
  const placed = rect
    ? {
        left: `${rect.x * 100}%`,
        top: `${rect.y * 100}%`,
        width: `${rect.width * 100}%`,
        height: `${rect.height * 100}%`,
      }
    : { left: "9%", top: "9%", width: "82%", aspectRatio: String(CARD_ASPECT) };

  /** Drag in fractions of the picture, so the rectangle moves with the finger. */
  const drag = (event: React.PointerEvent, apply: (dx: number, dy: number) => void) => {
    const area = box.current?.parentElement?.getBoundingClientRect();
    if (!area) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    let lastX = event.clientX;
    let lastY = event.clientY;
    const move = (step: PointerEvent) => {
      apply((step.clientX - lastX) / area.width, (step.clientY - lastY) / area.height);
      lastX = step.clientX;
      lastY = step.clientY;
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  };

  const ratio = rect && rect.height > 0 ? rect.width / rect.height : 1;

  return (
    <div
      className="absolute inset-0 touch-none"
      onPointerDown={(event) => {
        if (!onPan) return;
        drag(event, onPan);
      }}
    >
      <div
        ref={box}
        data-testid="guide-frame"
        onPointerDown={(event) => {
          if (!onMove) return;
          event.stopPropagation();
          drag(event, onMove);
        }}
        className="absolute touch-none rounded-[var(--radius-md)] border-2 transition-colors"
        style={{
          ...placed,
          borderColor: colour,
          // The ring thickens as the frame settles, so the shot never comes as a surprise.
          boxShadow: `0 0 0 ${Math.round(progress * 5)}px color-mix(in oklch, ${colour} 40%, transparent)`,
        }}
      >
        {readiness === "off" ? null : (
          // What the shutter waits for, with the time left to hold still as a bar.
          <span className="pointer-events-none absolute inset-x-0 -bottom-12 flex justify-center">
            <span className="cam-pill whitespace-nowrap">
              {t(`scan.guide.${readiness}`)}
              <span className="block h-[5px] w-12 overflow-hidden rounded-full bg-white/25">
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${String(Math.round(progress * 100))}%`, background: colour }}
                />
              </span>
            </span>
          </span>
        )}
        {onResize && rect ? (
          <button
            type="button"
            aria-label={t("scan.resizeFrame")}
            onPointerDown={(event) => {
              event.stopPropagation();
              drag(event, (dx) => {
                onResize(dx, ratio);
              });
            }}
            // Big enough for a finger, drawn small: the corner is a handle, not a button.
            className="absolute -right-5 -bottom-5 size-10 touch-none rounded-full border-2 bg-(--surface)/70"
            style={{ borderColor: colour }}
          />
        ) : null}
      </div>
    </div>
  );
}
